import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save, CheckCircle2, XCircle, AlertTriangle, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { calcReporte, GESTION, FIJA } from "@/lib/reporteCalc";

const fmtNum = (v, d = 2) => (v == null ? "—" : v.toLocaleString("es-MX", { minimumFractionDigits: d, maximumFractionDigits: d }));
const fmtMx = (v) => (v == null ? "—" : `${v < 0 ? "-" : ""}$${fmtNum(Math.abs(v), 0)}`);
const inputCls = "h-8 w-full min-w-[96px] rounded-md border border-slate-300 bg-white px-2 text-sm text-right tabular-nums text-slate-900 focus:outline-none focus:ring-2 focus:ring-red-200 disabled:bg-slate-50 disabled:border-slate-200";

function Cap({ vals, k, col, set, canEdit, max, step = "0.01", bg = "" }) {
  const v = vals[k]?.[col];
  return (
    <input type="number" min={0} max={max} step={step} value={v ?? ""} disabled={!canEdit} data-testid={`reporte-input-${k}-${col}`}
      onKeyDown={(e) => { if (e.key === "-" || e.key === "e") e.preventDefault(); }}
      onChange={(e) => set(k, col, e.target.value === "" ? null : Math.min(max ?? Infinity, Math.max(0, Number(e.target.value))))}
      className={`${inputCls} ${bg}`} />
  );
}

const Out = ({ v, money, pct, testid, bold, muted }) => (
  <span data-testid={testid} className={`tabular-nums ${bold ? "font-bold" : ""} ${v != null && v < 0 ? "text-red-600" : muted ? "text-[#767171]" : "text-slate-900"}`}>
    {money ? fmtMx(v) : pct ? (v == null ? "—" : `${fmtNum(v)}%`) : fmtNum(v)}
  </span>
);

function Cell({ spec, col, r, ctx, fill }) {
  const cls = `px-2 py-1.5 ${fill ? "bg-[#E7E6E6]" : ""}`;
  if (spec.input) return <td className={cls}><Cap {...ctx} k={spec.input} col={col} max={spec.max} step={spec.step} /></td>;
  const v = r[spec.k]?.[spec.f];
  return <td className={`${cls} text-right`}><Out v={v} money={spec.money} pct={spec.pct} bold={spec.bold} muted={spec.k === "hg_pct"} testid={`reporte-${spec.k}-${spec.f}-${col}`} /></td>;
}

const kgOut = (k, o = {}) => ({ k, f: "kg", ...o });
const impOut = (k, o = {}) => ({ k, f: "imp", money: true, ...o });
const DESDE_GF = { impuestos: "Gasto Fijo · Impuestos Federales", comisiones_bancarias: "Gasto Fijo · Deuda Bancaria", gasto_corporativo: "Gasto Fijo · Corporativos", inversiones: "Gasto Fijo · Inversiones", presupuesto: "Gestión Planta · Presupuesto Planta", extraordinarios: "Gestión Planta · Extraordinarios" };
const autoRow = (k, label) => ({ id: k, label, auto: DESDE_GF[k], plan: [kgOut(k), impOut(k)], real: [kgOut(k), { input: k }] });
const ROWS = [
  { block: "Bloque 1 • Mercado", sub: "Variable fuera de nuestro control" },
  { id: "cne", label: "Precio CNE", auto: "Precios de venta (CNE)", plan: [kgOut("cne"), impOut("cne")], real: [kgOut("cne"), impOut("cne")] },
  { id: "cif", label: "Costo CIF (Mont Belvieu•PEMEX)", plan: [{ input: "cif" }, impOut("cif")], real: [{ input: "cif" }, impOut("cif")] },
  { id: "flete", label: "Flete Terrestre", auto: "Precios de flete", plan: [kgOut("flete"), impOut("flete")], real: [kgOut("flete"), impOut("flete")] },
  { id: "margen", label: "Margen de Distribución", total: true, plan: [kgOut("margen", { bold: true }), impOut("margen", { bold: true })], real: [kgOut("margen", { bold: true }), impOut("margen", { bold: true })] },
  { block: "Bloque 2 • Gestión", sub: "Variable bajo nuestra gestión" },
  ...GESTION.map(([k, label]) => (DESDE_GF[k] ? autoRow(k, label) : { id: k, label, plan: [kgOut(k), { input: k }], real: [kgOut(k), { input: k }] })),
  { id: "hg", label: "HG $", plan: [kgOut("hg"), impOut("hg")], real: [kgOut("hg"), impOut("hg")] },
  { id: "hg_pct", label: "HG %/Kilos", plan: [{ input: "hg_pct", max: 100 }, kgOut("hg_pct", { f: "imp" })], real: [{ input: "hg_pct", max: 100 }, kgOut("hg_pct", { f: "imp" })] },
  { id: "cartera", label: "Cartera", plan: [kgOut("cartera"), { input: "cartera" }], real: [kgOut("cartera"), { input: "cartera" }] },
  { id: "costo_op", label: "Costo de Operación", total: true, plan: [kgOut("costo_op", { bold: true }), impOut("costo_op", { bold: true })], real: [kgOut("costo_op", { bold: true }), impOut("costo_op", { bold: true })] },
  { block: "Bloque 3 • Fija", sub: "Fija bajo nuestra decisión - Lo mensual se pondera por semana" },
  ...FIJA.map(([k, label]) => (DESDE_GF[k] ? autoRow(k, label) : { id: k, label, plan: [kgOut(k), { input: k }], real: [kgOut(k), { input: k }] })),
  { id: "total_fijo", label: "Total Gasto Fijo", total: true, plan: [kgOut("total_fijo", { bold: true }), impOut("total_fijo", { bold: true })], real: [kgOut("total_fijo", { bold: true }), impOut("total_fijo", { bold: true })] },
  { block: "Resultado", sub: "Debe cubrir la utilidad base" },
  { id: "utilidad", label: "Utilidad", gray: true, plan: [kgOut("utilidad", { bold: true }), impOut("utilidad", { bold: true })], real: [kgOut("utilidad", { bold: true }), impOut("utilidad", { bold: true })] },
  { id: "utilidad_base", label: "Utilidad Base (Según el Negocio)", plan: [{ input: "utilidad_base" }, impOut("utilidad_base")], real: [kgOut("utilidad_base"), impOut("utilidad_base")] },
  { id: "margen_gest", label: "Margen Gestionable (Límite bloque 2)", gray: true, plan: [kgOut("margen_gest", { bold: true }), impOut("margen_gest", { bold: true })], real: [kgOut("margen_gest", { bold: true }), impOut("margen_gest", { bold: true })] },
];

function Row({ row, calc, ctx }) {
  if (row.block) {
    return (
      <tr className="border-t-2 border-slate-200 bg-white">
        <td colSpan={5} className="px-3 pt-4 pb-1.5 text-[#375623] font-bold"><p className="text-sm">{row.block}</p><p className="text-xs">{row.sub}</p></td>
      </tr>
    );
  }
  return (
    <tr className={`border-t border-slate-200 ${row.total || row.gray ? "font-semibold" : ""}`} data-testid={`reporte-row-${row.id}`}>
      <td className={`px-3 py-1.5 whitespace-nowrap ${row.total ? "bg-[#767171] text-white" : row.gray ? "bg-[#E7E6E6] text-slate-900" : "text-slate-900"}`}>{row.label}{row.auto && <span className="ml-2 text-[10px] font-normal italic text-slate-500">({row.auto})</span>}</td>
      {row.plan.map((s, i) => <Cell key={`p${i}`} spec={s} col="plan" r={calc.plan} ctx={ctx} fill={row.total || row.gray} />)}
      {row.real.map((s, i) => <Cell key={`r${i}`} spec={s} col="real" r={calc.real} ctx={ctx} fill={row.total || row.gray} />)}
    </tr>
  );
}

function Veredicto({ liberada }) {
  if (liberada == null) return <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800" data-testid="reporte-veredicto"><AlertTriangle className="w-4 h-4" />Faltan datos de Precio CNE o Flete terrestre para evaluar la semana.</div>;
  return liberada
    ? <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-800" data-testid="reporte-veredicto"><CheckCircle2 className="w-4 h-4" />Resultado es MAYOR ó IGUAL a la utilidad Base: SE LIBERA LA SEMANA</div>
    : <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-800" data-testid="reporte-veredicto"><XCircle className="w-4 h-4" />Resultado es MENOR a la utilidad Base: NO ES APROBADA LA SEMANA</div>;
}

const semanaTxt = (d) => {
  const s = new Date(`${d.semana_inicio}T00:00:00`), e = new Date(`${d.semana_fin}T00:00:00`);
  const f = (x, o) => x.toLocaleDateString("es-MX", o).toUpperCase();
  return `SEMANA DEL ${f(s, { weekday: "long" })} ${s.getDate()} ${s.getMonth() !== e.getMonth() ? `DE ${f(s, { month: "long" })} ` : ""}AL ${f(e, { weekday: "long" })} ${e.getDate()} DE ${f(e, { month: "long" })} DEL ${e.getFullYear()}`;
};

export function ReporteEditor({ planta, year, week, structure, canEdit, onDirtyChange }) {
  const qc = useQueryClient();
  const [vals, setVals] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["reporte", "detalle", planta, year, week],
    queryFn: async () => (await api.get("/control-semanal/reporte", { params: { planta, year, week, structure } })).data,
  });
  useEffect(() => { if (data) { setVals(data.valores); } }, [data]);
  const changed = !!data && !!vals && JSON.stringify(vals) !== JSON.stringify(data.valores);
  useEffect(() => { onDirtyChange(planta, changed); }, [changed, planta, onDirtyChange]);
  useEffect(() => () => { onDirtyChange(planta, false); }, [planta, onDirtyChange]);
  if (isLoading || !vals) return <div className="py-6 pl-10"><Loader2 className="w-5 h-5 text-red-600 animate-spin" /></div>;
  const set = (k, col, v) => setVals({ ...vals, [k]: { ...vals[k], [col]: v } });
  const calc = calcReporte(vals, data.cne.precio_kg, data.flete.flete_kg);
  const ctx = { vals, set, canEdit };
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/control-semanal/reporte", { planta, structure, year, week, valores: vals });
      toast.success(`${planta}: reporte guardado (semana ${week} de ${year}).`);
      qc.invalidateQueries({ queryKey: ["reporte"] });
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo guardar.");
    } finally { setSaving(false); }
  };
  const exportar = async () => {
    setExporting(true);
    try {
      const res = await api.get("/control-semanal/reporte/export", { params: { planta, year, week, structure }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte_control_${planta.replace(/[\\/ ]+/g, "_")}_${year}_S${String(week).padStart(2, "0")}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo exportar el reporte.");
    } finally { setExporting(false); }
  };
  return (
    <div className="px-6 py-5 space-y-4" data-testid={`reporte-editor-${planta}`}>
      <div className="max-w-7xl mx-auto space-y-3">
        <div className="text-center rounded-xl overflow-hidden border border-slate-200">
          <p className="bg-[#0070C0] text-white text-lg font-bold py-1.5">{planta}</p>
          <div className="bg-[#DEEBF7] text-black py-1.5 text-xs space-y-0.5">
            <p>SISTEMA DE CONTROL DE RENTABILIDAD Y SUMINISTRO</p>
            <p>CONTROL SEMANAL DE PRESUPUESTO Y RENTABILIDAD</p>
            <p className="font-bold" data-testid="reporte-semana-titulo">{semanaTxt(data)}</p>
          </div>
        </div>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full min-w-[820px] text-sm" data-testid={`reporte-table-${planta}`}>
              <thead className="text-[11px] uppercase tracking-wider text-slate-500">
                <tr><th rowSpan={2} className="text-left px-3 py-2 bg-white text-black font-bold">Concepto</th><th colSpan={2} className="px-3 py-2 border-l border-white bg-[#DEEBF7] text-[#7F6000] font-bold">Planeado</th><th colSpan={2} className="px-3 py-2 border-l border-white bg-[#DEEBF7] text-[#7F6000] font-bold">Real ejecutado</th></tr>
                <tr className="bg-[#2F75B5] text-white font-bold"><th className="px-2 py-1.5 text-right border-l border-white">Por kilo</th><th className="px-2 py-1.5 text-right">Importe</th><th className="px-2 py-1.5 text-right border-l border-white">Por kilo</th><th className="px-2 py-1.5 text-right">Importe</th></tr>
              </thead>
              <tbody>
                <tr className="border-t border-slate-200" data-testid="reporte-row-venta_kg">
                  <td className="px-3 py-2 font-bold text-black whitespace-nowrap">Venta en Kilos<span className="ml-2 text-[10px] font-normal italic text-slate-500">(Gestión Planta · Variables de Gestión)</span>
                    {data.venta?.motivo && <span className="block text-[10px] font-normal text-amber-700" data-testid="reporte-venta-motivo">{data.venta.motivo}</span>}</td>
                  {["plan", "real"].map((c) => (
                    <td key={c} colSpan={2} className="px-3 py-1.5 bg-[#E7E6E6] text-center font-bold tabular-nums text-slate-900" data-testid={`reporte-input-venta_kg-${c}`}>{fmtNum(vals.venta_kg?.[c], 2)}</td>
                  ))}
                </tr>
                {ROWS.map((row, i) => <Row key={row.id || `b${i}`} row={row} calc={calc} ctx={ctx} />)}
              </tbody>
            </table>
          </div>
          <Veredicto liberada={calc.liberada} />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[11px] text-slate-500">Los campos con recuadro se capturan; los datos marcados entre paréntesis vienen de otro módulo; el resto se calcula.</p>
        <div className="flex items-center gap-3">
          {changed && <span className="text-[11px] font-semibold text-amber-700" data-testid={`reporte-unsaved-${planta}`}>Cambios sin guardar{" · guarda para incluirlos en el Excel"}</span>}
          <button onClick={exportar} disabled={exporting} data-testid={`reporte-export-${planta}`} className="h-9 px-4 rounded-lg border border-emerald-600 text-emerald-700 bg-white text-sm font-semibold hover:bg-emerald-50 disabled:opacity-50 flex items-center gap-2">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Exportar a Excel
          </button>
          {canEdit && (
            <button onClick={save} disabled={saving || !changed} data-testid={`reporte-save-${planta}`} className="h-9 px-4 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 flex items-center gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Guardar reporte
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
